import { defineComponent as _defineComponent } from 'vue'
import { computed, nextTick, onMounted, ref, toRefs, watch, Ref } from 'vue';
import { useScroll, watchThrottled } from '@vueuse/core';
import { useChat, getNumericTimetoken } from '~/composables/useChat';
import { IEnvelope, Timetoken } from '~/types/chat';
import Message from './Message.vue';
import NoticeCard from './NoticeCard.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'MessageList',
  emits: ["focusInput"],
  setup(__props, { emit }: { emit: ({
  (event: 'focusInput'): void;
}), expose: any, slots: any, attrs: any }) {



const {
  isFetchingMessages,
  lastReadTimetoken: lastReadTimetokenFromMembership,
  messages,
  pubnub,
  setLastReadTimetoken,
} = useChat();
const nextReadTimetoken = ref<Timetoken>(
  lastReadTimetokenFromMembership.value ?? '',
);
const lastReadTimetoken = ref<Timetoken>(
  lastReadTimetokenFromMembership.value ?? '',
);
const lastReadTimetokenOnLoad = ref<Timetoken>(
  lastReadTimetokenFromMembership.value ?? '',
);

const hasFetchedInitialMessages = ref(false);
const messagesContainer = ref<Vue>();
const messagesEl = computed(() =>
  messagesContainer.value?.$el
    ? (messagesContainer.value?.$el as HTMLUListElement)
    : undefined,
);
const { arrivedState, directions } = useScroll(messagesEl);
const { top: hasArrivedTop, bottom: hasArrivedBottom } = toRefs(arrivedState);
const { top: toTop } = toRefs(directions);
const hasStickyScroll = ref(false);
const hasInitiallyScrolled = ref(false);
const yOffset = 32;
const isLastReadMessageFetched = ref(false);
const showLastReadDivider = ref(true);
const unreadMessageCount = ref(0);
const showBottomOverlay = ref(false);
const showUnreadCount = computed(
  () => unreadMessageCount.value > 0 && hasInitiallyScrolled.value,
);

const updateUnreadMessageCount = () => {
  unreadMessageCount.value =
    messagesEl.value?.querySelectorAll(
      `[data-timetoken="${lastReadTimetoken.value}"]~li>.chat-message`,
    )?.length ?? 0;
};

const updateShowBottomOverlay = () => {
  showBottomOverlay.value =
    (messagesEl.value?.scrollTop ?? 0) + (messagesEl.value?.clientHeight ?? 0) <
    (messagesEl.value?.scrollHeight ?? 0);
};

const hasMessage = ({ message }: IEnvelope) => {
  return !!message?.text;
};

const scrollToBottom = () => {
  if (
    messagesEl.value &&
    messagesEl.value.scrollHeight > messagesEl.value.clientHeight
  ) {
    messagesEl.value.scroll({
      top: messagesEl.value.scrollHeight,
      behavior: 'smooth',
    });

    if (showLastReadDivider.value) {
      showLastReadDivider.value = false;
    }
  }
};

const scrollToMessage = (timetoken: string | number) => {
  if (messagesEl.value && timetoken) {
    const message = messagesEl.value?.querySelector(
      `[data-timetoken="${timetoken}"]`,
    );
    if (!message) {
      return;
    }
    const { top: containerTop } = messagesEl.value.getBoundingClientRect();
    const { top: messageTop } = message.getBoundingClientRect();
    messagesEl.value.scroll({
      top: messageTop - containerTop - yOffset / 2,
      behavior: 'smooth',
    });
  }
};

const handleMessageDeletedOrUndeleted = (timetoken: string) => {
  // setTimeout rather than nextTick bc of transition
  setTimeout(() => {
    updateUnreadMessageCount();
    updateShowBottomOverlay();
    if (showBottomOverlay.value && hasStickyScroll.value) {
      hasStickyScroll.value = false;
    }
  }, 400);
};
const handleMessageEdited = () => {
  nextTick(() => {
    updateShowBottomOverlay();
    if (showBottomOverlay.value && hasStickyScroll.value) {
      hasStickyScroll.value = false;
    }
  });
};

const handleMessageIntersected = ({
  timetoken,
  intersectionRatio,
}: {
  timetoken: Timetoken;
  intersectionRatio: number;
}) => {
  const newTimetoken = getNumericTimetoken(timetoken);
  const lastReadNumeric = getNumericTimetoken(lastReadTimetoken.value);
  // if the message is fully in view, set it as the last read timetoken
  if (intersectionRatio === 1) {
    if (newTimetoken > lastReadNumeric) {
      nextReadTimetoken.value = timetoken;
      lastReadTimetoken.value = timetoken;
      updateUnreadMessageCount();
    }
  }
  // it's possible for nextReadTimetoken to be empty when lastReadTimetoken is not
  // (i.e. when intially loading and no messages have been read, last read is set to the last message)
  // so we check both
  else if (
    newTimetoken > getNumericTimetoken(nextReadTimetoken.value) &&
    newTimetoken > lastReadNumeric
  ) {
    nextReadTimetoken.value = timetoken;
  }
};

const handleMessageMounted = (timetoken: Timetoken) => {
  updateShowBottomOverlay();

  if (!hasStickyScroll.value) {
    updateUnreadMessageCount();
  }

  if (hasInitiallyScrolled.value) {
    return;
  }

  if (
    lastReadTimetoken.value.toString() === timetoken.toString() &&
    !hasInitiallyScrolled.value
  ) {
    setTimeout(() => {
      scrollToMessage(timetoken);
      hasInitiallyScrolled.value = true;
      if (
        getNumericTimetoken(lastReadTimetokenOnLoad.value) >=
        getNumericTimetoken(messages?.value?.[0]?.timetoken)
      ) {
        isLastReadMessageFetched.value = true;
      }
    }, 150);
  }
};

watch(
  isFetchingMessages,
  (current, prev) => {
    // handle edge cases w/ last read timetoken on intial load
    if (!current && prev) {
      // set intial fetch flag
      if (!hasFetchedInitialMessages.value) {
        hasFetchedInitialMessages.value = true;
      }
      // default to last message in the list if no saved last read value
      if (!lastReadTimetoken.value) {
        lastReadTimetoken.value =
          messages.value[messages.value.length - 1]?.timetoken;
        hasStickyScroll.value = true;
        return;
      }

      // last read message is the last message
      if (
        messages?.value?.length &&
        messages.value[messages.value.length - 1]?.timetoken ===
          lastReadTimetoken.value
      ) {
        hasStickyScroll.value = true;
        return;
      }

      // last read message is earlier than the earliest loaded message
      if (
        messages.value?.length &&
        lastReadTimetoken.value &&
        getNumericTimetoken(messages.value[0].timetoken) >
          getNumericTimetoken(lastReadTimetoken.value)
      ) {
        lastReadTimetoken.value = messages.value[0].timetoken;
      }
    }
  },
  { immediate: true },
);

watch(
  [toTop, hasArrivedBottom],
  ([currentToTop, currentArrivedBottom], [previousToTop]) => {
    // Turn off sticky scroll if scrolling up & not still at the bottom
    if ((previousToTop || currentToTop) && !currentArrivedBottom) {
      if (hasStickyScroll.value) {
        hasStickyScroll.value = false;
      }
    }
  },
);

watch(hasArrivedBottom, (current, previous) => {
  if (current && !previous) {
    showBottomOverlay.value = false;

    if (showLastReadDivider.value) {
      showLastReadDivider.value = false;
    }

    // If next read is greater than last read, update last read
    if (
      getNumericTimetoken(nextReadTimetoken.value) >
      getNumericTimetoken(lastReadTimetoken.value)
    ) {
      lastReadTimetoken.value = nextReadTimetoken.value;
      updateUnreadMessageCount();
    }

    // Turn on sticky scroll when scrolled to bottom
    if (!hasStickyScroll.value) {
      hasStickyScroll.value = true;
    }
  }

  if (!current && previous) {
    showBottomOverlay.value = true;
  }
});

watch(nextReadTimetoken, (newVal, oldVal) => {
  if (
    getNumericTimetoken(lastReadTimetoken.value) >= getNumericTimetoken(oldVal)
  ) {
    return;
  }
  lastReadTimetoken.value = oldVal;
  if (!isLastReadMessageFetched.value) {
    isLastReadMessageFetched.value = true;
  }
  updateUnreadMessageCount();
});

watchThrottled<string | undefined, true>(
  lastReadTimetoken,
  async (newVal, oldVal) => {
    if (newVal) {
      await setLastReadTimetoken(newVal);
    }
  },
  { throttle: 2000, trailing: true, leading: true, immediate: true },
);

watch(
  // Messages never actually get removed, they just get hidden.
  // So this is a good way to detect when a new message is added to the list.
  () => messages.value.length,
  () => {
    if (!hasInitiallyScrolled.value) {
      return;
    }

    nextTick(() => {
      nextTick(() => {
        // double nextTick to ensure the DOM is updated
        if (hasStickyScroll.value) {
          scrollToBottom();
        }
      });
    });
  },
  { flush: 'post', immediate: true },
);

return { __sfc: true,emit, isFetchingMessages, lastReadTimetokenFromMembership, messages, pubnub, setLastReadTimetoken, nextReadTimetoken, lastReadTimetoken, lastReadTimetokenOnLoad, hasFetchedInitialMessages, messagesContainer, messagesEl, arrivedState, directions, hasArrivedTop, hasArrivedBottom, toTop, hasStickyScroll, hasInitiallyScrolled, yOffset, isLastReadMessageFetched, showLastReadDivider, unreadMessageCount, showBottomOverlay, showUnreadCount, updateUnreadMessageCount, updateShowBottomOverlay, hasMessage, scrollToBottom, scrollToMessage, handleMessageDeletedOrUndeleted, handleMessageEdited, handleMessageIntersected, handleMessageMounted, Message, NoticeCard }
}

})